'use client';
import React, { useRef, useEffect, useState } from 'react';
import SecondaryButton from '@/components/ui/SecondaryButton/SecondaryButton';
import Headline from '@/components/ui/Headline/Headline';
import PrimaryButton from '@/components/ui/PrimaryButton/PrimaryButton';
import BrandSunMotif from '@/components/ui/BrandSunMotifs/BrandSunMotif';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useAspectRatio from '@/hooks/useAspectRatio';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

import { LanguageType, LanguageDirection, ServiceCategorySectionContent } from '@/lib/types';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import CompactButton from '@/components/ui/CompactButton/CompactButton';
import { ArrowUpRight } from '@phosphor-icons/react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { useLenis } from '@/contexts/LenisContext';
import { debug } from '@/lib/debug';
interface OverviewSectionProps {
  language?: LanguageType
  serviceCategorySectionPageData: ServiceCategorySectionContent
  subheadingText?: string
  headingText?: string
  paragraphText?: string
}


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const LANGUAGE_TO_FLEX_DIRECTION: Record<LanguageType, string> = {
  english: "justify-start items-start",
  arabic: "justify-end items-end"
};

const OverviewSection: React.FC<OverviewSectionProps> = ({
  language = "english",
  serviceCategorySectionPageData,
  subheadingText = "RIVIVE",
  headingText = "Treatments designed around you",
  paragraphText = "Discover our personalized treatments that nurture your natural beauty, blending advanced science with serenity and luxury for a truly transformative experience."
}) => {
  const navigate = useAnimatedNavigate();
  const pathname = usePathname();
  const { lenis } = useLenis();
  const isLg = useIsLg();
  const isNarrowDesktop = useMediaQuery('(min-width: 992px) and (max-width: 1050px)');
  const sectionRef = useRef<HTMLElement>(null);
  // IMPORTANT: Keep containerRef separate and only used when isLg is true
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const activeCardIndexRef = useRef(activeCardIndex);

  // const cards = [
  //   {
  //     title: "Lorem ipsum dolor sit amet 1",
  //     description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 1",
  //     image: "https://picsum.photos/seed/1/800/600"
  //   },
  //   {
  //     title: "Lorem ipsum dolor sit amet 2",
  //     description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 2",
  //     image: "https://picsum.photos/seed/2/800/600"
  //   },
  //   {
  //     title: "Lorem ipsum dolor sit amet 3",
  //     description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 3",
  //     image: "https://picsum.photos/seed/3/800/600"
  //   },
  //   {

  //     title: "Lorem ipsum dolor sit amet 4",
  //     description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 4",
  //     image: "https://picsum.photos/seed/4/800/600"
  //   },
  // ];
  const cards = [...serviceCategorySectionPageData.categories].sort((a, b) => a.displayOrder - b.displayOrder).map((category) => ({
    title: category.serviceCategoryTitle,
    description: category.serviceCategoryOverviewDescription,
    image: category.serviceCategoryOverviewImage.url,
    altText: category.serviceCategoryOverviewImageAltText,
    ctaButtonText: category.serviceCategoryCTAButtonText,
    navigationLabel: category.serviceCategoryNavigationLabel
  }));

  const handleServiceClick = (serviceTitle: string) => {
    debug.log('state handleServiceClick', serviceTitle);
    // Find the card by title to get its navigationLabel
    const card = cards.find(card => card.title === serviceTitle);
    if (!card?.navigationLabel) {
      debug.log('state card not found', serviceTitle);
      return;
    }

    const navigationLabel = card.navigationLabel;
    const isServicesPage = pathname === '/services';

    if (isServicesPage) {
      const serviceSection = document.getElementById(navigationLabel);
      if (serviceSection) {
        const yOffset = isLg ? -100 : -50;
        const y = serviceSection.getBoundingClientRect().top + window.scrollY + yOffset;
        lenis?.scrollTo(y, { offset: 0, immediate: false, duration: 1.5 });
      }
    } else {
      debug.log('state navigate to services', navigationLabel);
      navigate('/services', { state: { scrollToService: true, serviceNavigationLabel: navigationLabel } });
    }
  };

  // Keep the ref in sync with the state
  useEffect(() => {
    activeCardIndexRef.current = activeCardIndex;
  }, [activeCardIndex]);


  // --- GSAP Logic moved inside the conditional render ---


  return (
    <section className='
      w-full
      padding-main-x-mobile
      padding-main-x-desktop
      pt-[1rem]
      tb:pt-0
      flex flex-col items-center justify-center
      gap-y-[2.5rem] lg:gap-y-[6rem]
      '
      ref={sectionRef}>
      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className={`
        w-full
        max-w-full
        container-large
        px-[0.5rem]
        lg:px-0
        `}
      >

        <div
          style={{ direction: "ltr" }}
          className={`
            w-full flex ${LANGUAGE_TO_FLEX_DIRECTION[language]}
          `}>
          <Headline
            subheading={subheadingText}
            heading={headingText}
            paragraph={paragraphText}
            type='short'
            alignment={language === "english" ? 'left' : 'right'}
            language={language}
          />
        </div>
      </div>


      {isLg ? (
        // --- Desktop Layout with GSAP ---
        <div className="w-full  container-x-large  flex flex-col items-center justify-center">
          <div
            ref={containerRef}
            //  h-[53.5rem]
            className="
              w-full
              flex flex-row gap-[1rem] relative"
            style={{
              direction: LANGUAGE_TO_DIRECTION[language],
              height: `min(calc(100svh - 4rem), 53.5rem)` // 2rem top and bottom padding, max 856px
            }}
          >
            {/* Static text card */}
            <div
              style={{
                height: `min(calc(100svh - 4rem), 53.5rem)` // 2rem top and bottom padding, max 856px
              }}
              className="
              w-full min-w-[30.6875rem] max-w-[37rem]
              flex flex-col items-center justify-between
              bg-bg-light-primary
              outline-secondary outline-stroke-soft-200 border-radius-main
              ">
              <div className="w-full flex flex-col gap-[1.5rem] items-center justify-center px-[3.5rem] pt-[4.5rem] pb-[2rem]">
                <BrandSunMotif type='sunshine' />
                <h3 className='text-36-to-24--neue-june text-text-primary text-center text-content'>
                  {cards[activeCardIndex].title}
                </h3>
              </div>

              <div className="w-full flex flex-col gap-[2rem] items-center justify-center px-[3.5rem] pt-[4.5rem] pb-[2rem]">
                <p className='text-18--alexandria text-text-tertiary text-center text-content'>
                  {cards[activeCardIndex].description}
                </p>
                <div style={{ direction: LANGUAGE_TO_DIRECTION[language] }} className='w-full cta-button-content flex flex-col items-center justify-center gap-[0.5rem] lg:gap-[0.75rem]'>
                  <PrimaryButton text={cards[activeCardIndex].ctaButtonText} icon={false} size={64} type='accent' language={language} onClick={() => navigate('/signup')} />
                  <SecondaryButton
                    text={language === 'arabic' ? "اعرفوا المزيد" : "Learn More"}
                    size={64}
                    style='neutral'
                    language={language}
                    radius={20}
                    onClick={() => handleServiceClick(cards[activeCardIndex].title)}
                  />
                </div>
              </div>
            </div>

            {/* Image container with absolute positioning */}
            <div
              style={{
                height: `min(calc(100svh - 4rem), 53.5rem)` // 2rem top and bottom padding, max 856px
              }}
              className="
             w-full overflow-hidden
             relative
             border-radius-main
             outline-secondary outline-stroke-soft-200
             ">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="card-image w-full h-full absolute top-0 right-0 border-radius-main outline-secondary outline-stroke-soft-200 overflow-hidden"
                >
                  <Image
                    src={card.image}
                    alt={card.altText}
                    fill
                    sizes="(min-width: 992px) 50vw, 100vw"
                    loading="eager"
                    style={{
                      objectFit: 'cover',
                      objectPosition: isNarrowDesktop
                        ? ['25% center', '40% center', '75% center', 'center'][index]
                        : undefined
                    }}
                  />
                </div>
              ))}
            </div>

            {/* --- GSAP Setup within the desktop layout --- */}
            <UseGSAPSetup
              containerRef={containerRef}
              cardsLength={cards.length}
              setActiveCardIndex={setActiveCardIndex}
              activeCardIndexRef={activeCardIndexRef}
            />
          </div>
        </div>
      ) : (
        // --- Mobile Layout without GSAP animation/pinning ---
        <div className="w-full flex flex-col gap-[1rem]">
          {cards.map((card, index) => (
            <div
              key={index}
              style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
              className='
                w-full
                flex flex-col items-center justify-between gap-[1rem]
                bg-bg-light-primary
                outline-secondary outline-stroke-soft-200
                border-radius-main
                overflow-hidden
                '>
              <div
                data-mobile-text
                className='
                  flex flex-col gap-[1.5rem]
                  px-[2rem]
                  pt-[2.5rem]
                  pb-[2rem]
                  w-full
                  '>
                <div className='
                    flex flex-col gap-[1.5rem] items-center justify-center
                    '>
                  <BrandSunMotif type='sunshine' />
                  <h3 className='text-36-to-24--neue-june text-text-primary text-center'>
                    {card.title}
                  </h3>
                </div>

                <p className='text-18--alexandria text-text-tertiary text-center'>
                  {card.description}
                </p>

              </div>
              <div className='
                    !outline-secondary
                    !outline-stroke-soft-200
                    border-radius-main
                    overflow-hidden
                    relative
                    w-full
                    aspect-square
                    tb:aspect-5/3
                  '>
                <Image
                  src={card.image}
                  alt={`Card ${index + 1}`}
                  fill
                  sizes="100vw"
                  loading="eager"
                  style={{ objectFit: 'cover' }}
                />

                <div
                  data-mobile-button
                  className='
                    absolute
                    bottom-[1.25rem] left-[1rem] right-[1rem]
                  '>
                    <div style={{ direction: LANGUAGE_TO_DIRECTION[language] }} className='flex flex-row items-center justify-center gap-[0.5rem] lg:gap-[0.75rem]'>
                      {/* <SecondaryButton text="Learn More" size={52} style='neutral' language={language} radius={16} onClick={() => navigate('/services')} /> */}
                      <PrimaryButton text={card.ctaButtonText} icon={false} size={52} type='accent' language={language} onClick={() => navigate('/signup')} />
                      <CompactButton size={44} radius={16} onClick={() => handleServiceClick(card.title)}>
                        <ArrowUpRight size={20} />
                      </CompactButton>
                    </div>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile GSAP Setup */}
          <MobileGSAPSetup cards={cards} />
        </div>
      )}
    </section>

  )
};

export default OverviewSection;


// New component to encapsulate GSAP setup
const UseGSAPSetup = ({ containerRef, cardsLength, setActiveCardIndex, activeCardIndexRef }: any) => {
  const aspectRatio = useAspectRatio();
  const [isContainerReady, setIsContainerReady] = useState(false);


  // Watch for containerRef changes
  useEffect(() => {
    // debug.log('useGSAPSetup useEffect containerRef.current', containerRef.current);
    if (containerRef.current) {
      // debug.log('useGSAPSetup useEffect containerRef.current true');
      setIsContainerReady(true);
    }
  }, [containerRef.current]);

  useGSAP(() => {
    // debug.log('useGSAPSetup useGSAP');
    const container = containerRef.current;
    if (container) {

      // debug.log('useGSAPSetup useGSAP container');
      const images = container.querySelectorAll('.card-image');
      const textElements = container.querySelectorAll('.text-content');
      const buttonElements = container.querySelectorAll('.cta-button-content');

      // Initial blur mask animation for text elements
      if (textElements.length > 0) {
        // Set initial states for blur mask animation
        gsap.set(textElements, {
          autoAlpha: 0,
          filter: 'blur(30px)',
          y: +100,
          force3D: true,
        });

        // Set initial states for button elements (no y translation)
        if (buttonElements.length > 0) {
          gsap.set(buttonElements, {
            autoAlpha: 0,
            filter: 'blur(30px)',
            force3D: true,
          });
        }

        // Create initial reveal animation
        const initialTl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        });

        // Animate text elements with stagger
        initialTl.to(textElements, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 1.0,
          stagger: 0.2,
          ease: "power2.out",
          force3D: true,
        });

        // Animate button elements in parallel (no stagger, no y translation)
        if (buttonElements.length > 0) {
          initialTl.to(buttonElements, {
            autoAlpha: 1,
            filter: 'blur(0px)',
            duration: 1.0,
            ease: "power2.out",
            force3D: true,
          }, "<0.4"); // Start at 0.4s to align with second text element
        }
      }

      images.forEach((image: any, i: any) => {
        gsap.set(image, {
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: i,
          y: i == 0 ? `0` : `${container.offsetHeight}px`,
        });

        if (i < images.length) {
          // Animation for the image sliding
          gsap.timeline({
            scrollTrigger: {
              trigger: container,
              start: `center+=${Math.max(0, i - 1) * container.offsetHeight} center`,
              end: `center+=${i * container.offsetHeight} center`,
              scrub: true,
              // markers: true,
            }
          }).to(image, {
            y: 0,
            duration: 1,
            ease: 'none',
            force3D: true,
          });

          // Updated content change trigger.
          // Fires when the incoming image is 30% slid in (was 75% — moved
          // earlier so the text reveals while the new image is still in motion).
          ScrollTrigger.create({
            trigger: container,
            start: `center+=${(Math.max(0, i - 1) + 0.30) * container.offsetHeight} center`,
            end: `center+=${(i + 0.30) * container.offsetHeight} center`,
            onEnter: () => {
              // debug.log('onEnter, activeCardIndexRef.current', activeCardIndexRef.current, i);
              if (activeCardIndexRef.current !== i) {
                // Animate text and button elements out in parallel
                const tlOut = gsap.timeline();
                tlOut.to(textElements, {
                  autoAlpha: 0,
                  filter: 'blur(15px)',
                  duration: 0.3,
                  ease: "power2.out",
                  force3D: true,
                });
                if (buttonElements.length > 0) {
                  tlOut.to(buttonElements, {
                    autoAlpha: 0,
                    filter: 'blur(15px)',
                    duration: 0.3,
                    ease: "power2.out",
                    force3D: true,
                  }, "<");
                }
                tlOut.call(() => {
                  setActiveCardIndex(i);
                  // Animate text and button elements back in parallel
                  const tlIn = gsap.timeline();
                  tlIn.fromTo(textElements, {
                    y: +50
                  }, {
                    autoAlpha: 1,
                    filter: 'blur(0px)',
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                    force3D: true,
                  });
                  if (buttonElements.length > 0) {
                    tlIn.to(buttonElements, {
                      autoAlpha: 1,
                      filter: 'blur(0px)',
                      duration: 0.7,
                      ease: "power2.out",
                      force3D: true,
                    }, "<");
                  }
                });
              }
            },
            onEnterBack: () => {
              // debug.log('onEnterBack, activeCardIndexRef.current', activeCardIndexRef.current, i);
              if (activeCardIndexRef.current !== i) {
                // Animate text and button elements out in parallel
                const tlOut = gsap.timeline();
                tlOut.to(textElements, {
                  autoAlpha: 0,
                  filter: 'blur(15px)',
                  duration: 0.3,
                  ease: "power2.out",
                  force3D: true,
                });
                if (buttonElements.length > 0) {
                  tlOut.to(buttonElements, {
                    autoAlpha: 0,
                    filter: 'blur(15px)',
                    duration: 0.3,
                    ease: "power2.out",
                    force3D: true,
                  }, "<");
                }
                tlOut.call(() => {
                  setActiveCardIndex(Math.max(0, i));
                  // Animate text and button elements back in parallel
                  const tlIn = gsap.timeline();
                  tlIn.fromTo(textElements, {
                    y: +50
                  }, {
                    autoAlpha: 1,
                    filter: 'blur(0px)',
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                    force3D: true,
                  });
                  if (buttonElements.length > 0) {
                    tlIn.to(buttonElements, {
                      autoAlpha: 1,
                      filter: 'blur(0px)',
                      duration: 0.7,
                      ease: "power2.out",
                      force3D: true,
                    }, "<");
                  }
                });
              }
            },
            onLeaveBack: () => {
              // debug.log('onLeaveBack, activeCardIndexRef.current', activeCardIndexRef.current, i - 1);
              if (activeCardIndexRef.current !== i - 1) {
                // Animate text and button elements out in parallel
                const tlOut = gsap.timeline();
                tlOut.to(textElements, {
                  autoAlpha: 0,
                  filter: 'blur(15px)',
                  duration: 0.3,
                  ease: "power2.out",
                  force3D: true,
                });
                if (buttonElements.length > 0) {
                  tlOut.to(buttonElements, {
                    autoAlpha: 0,
                    filter: 'blur(15px)',
                    duration: 0.3,
                    ease: "power2.out",
                    force3D: true,
                  }, "<");
                }
                tlOut.call(() => {
                  setActiveCardIndex(Math.max(0, i - 1));
                  // Animate text and button elements back in parallel
                  const tlIn = gsap.timeline();
                  tlIn.fromTo(textElements, {
                    y: +50
                  }, {
                    autoAlpha: 1,
                    filter: 'blur(0px)',
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                    force3D: true,
                  });
                  if (buttonElements.length > 0) {
                    tlIn.to(buttonElements, {
                      autoAlpha: 1,
                      filter: 'blur(0px)',
                      duration: 0.7,
                      ease: "power2.out",
                      force3D: true,
                    }, "<");
                  }
                });
              }
            }
          });
        }
      });

      ScrollTrigger.create({
        trigger: container,
        start: "center center",
        end: `+=${(cardsLength - 1) * container.offsetHeight + 0.25 * container.offsetHeight}px`,
        pin: true,
        pinSpacing: true,
        // anticipatePin: 1,
        // invalidateOnRefresh: true,
        // anticipatePin: 1,
      });

      // After all ScrollTriggers are created
      // i fucking hate gsap
      ScrollTrigger.sort();
      ScrollTrigger.refresh(); // Refresh immediately after sorting
    }


  }, { dependencies: [cardsLength, isContainerReady, aspectRatio], revertOnUpdate: true });

  return null;
}

// New component to handle mobile blur mask animations
const MobileGSAPSetup = ({ cards }: any) => {
  useGSAP(() => {
    const mobileTextElements = document.querySelectorAll('[data-mobile-text]');
    const mobileButtonElements = document.querySelectorAll('[data-mobile-button]');

    if (!mobileTextElements.length) return;

    // Set initial states for blur mask animation
    gsap.set(mobileTextElements, {
      autoAlpha: 0,
      filter: 'blur(20px)',
      y: +50,
      force3D: true,
    });

    // Set initial states for button elements (no y translation)
    if (mobileButtonElements.length > 0) {
      gsap.set(mobileButtonElements, {
        autoAlpha: 0,
        filter: 'blur(20px)',
        force3D: true,
      });
    }

    // Create scroll trigger animations for each mobile text element
    mobileTextElements.forEach((element) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: "top 90%",
          toggleActions: "play none none none"
        }
      }).to(element, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.7,
        ease: "power2.out",
        force3D: true,
      });
    });

    // Create separate scroll trigger animations for each mobile button element
    mobileButtonElements.forEach((element) => {
      gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: "top 90%",
          toggleActions: "play none none none"
        }
      }).to(element, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 0.7,
        ease: "power2.out",
        force3D: true,
      });
    });

  }, { dependencies: [cards.length], revertOnUpdate: true });

  return null;
}
